import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'antd';
import { ButtonCommon } from 'components/common';
import { BrandFormValues, CategoryItem, AddBrandModalProps } from 'types';
import BrandInfoSection from './BrandInfoSection';
import CategorySelectionSection from './CategorySelectionSection';
import { useGetAllCategoriesQuery } from 'services';

const AddBrandModal: React.FC<AddBrandModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  onGenerate,
  form,
  isSubmitting,
  editMode,
  isCopyMode = false,
}) => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>('');
  const [availableCategories, setAvailableCategories] = useState<CategoryItem[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>([]);
  const [checkedAvailableIds, setCheckedAvailableIds] = useState<string[]>([]);

  const { data: categoriesResponse } = useGetAllCategoriesQuery({
    query: { size: 'unlimited', include: 'parent' },
  });
  const [codePrefix, setCodePrefix] = useState<string>('');
  const [genCode, setGenCode] = useState<string>('');
  const [modalOpened, setModalOpened] = useState(false);
  const categories = useMemo<CategoryItem[]>(() => {
    if (!categoriesResponse?.success) return [];
    return categoriesResponse.data.rows.map((category) => ({
      id: category.id,
      name: category.name || '',
      code: category.code || '',
      isActive: category.isActive || false,
      description: category.description || '',
      createdAt: category.createdAt || new Date(),
    }));
  }, [categoriesResponse]);

  useEffect(() => {
    setAvailableCategories(categories);
  }, [categories]);

  useEffect(() => {
    if (isOpen) {
      setSearchValue('');
      const formValues = form.getFieldsValue(true);
      setIsActive(formValues.isActive !== false);

      if (!editMode && !isCopyMode) {
        setAvailableCategories(categories);
        setSelectedCategories([]);
      } else {
        const categoryIds = formValues.categories || [];
        let selectedCats: CategoryItem[] = [];

        if (formValues.categoryObjects && formValues.categoryObjects.length > 0) {
          selectedCats = formValues.categoryObjects.map((cat: CategoryItem) => {
            const fullCat = categories.find((c) => c.id === cat.id);
            return fullCat ? { ...fullCat } : cat;
          });
        } else if (categoryIds.length > 0) {
          selectedCats = categoryIds.map((id: string) => {
            const cat = categories.find((c) => c.id === String(id));
            return cat || ({ id: String(id), name: `Category ${id}` } as CategoryItem);
          });
        }
        setSelectedCategories(selectedCats);

        const selectedIds = new Set(selectedCats.map((cat) => cat.id));
        setAvailableCategories(categories.filter((cat) => !selectedIds.has(cat.id)));
      }
    }
  }, [isOpen, isCopyMode, categories, editMode]);

  const handleSubmit = (values: BrandFormValues): void => {
    onSubmit({
      ...values,
      categoryObjects: selectedCategories,
      categories: selectedCategories.map((cat) => cat.id),
    });
  };
  const handleCancel = () => {
    console.log('ok');
    setSearchValue('');
    setCheckedAvailableIds([]);
    setAvailableCategories([]);
    setSelectedCategories([]);
    form.resetFields();
    onCancel();
  };
  useEffect(() => {
    if (isOpen && !editMode) {
      setAvailableCategories(categories);
      setSelectedCategories([]);
    }
  }, [isOpen, categories]);
  useEffect(() => {
    const code = form.getFieldValue('code');

    if (genCode && !editMode) {
      const prefix = genCode.substring(0, 6);
      setCodePrefix(prefix);
      form.setFieldsValue({ code: genCode });
    }
    if (code) {
      const prefix = code.substring(0, 6);
      setCodePrefix(prefix);
    }
  }, [genCode, form, isOpen]);
  useEffect(() => {
    const fetchCode = async () => {
      if (isOpen && !modalOpened) {
        setModalOpened(true);

        if (!editMode) {
          const code = await onGenerate();

          setGenCode(code);
        }
      }

      if (!isOpen) {
        setModalOpened(false);
      }
    };
    fetchCode();
  }, [isOpen, editMode, onGenerate, modalOpened]);
  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={1800}
      centered
      className='add-brand-modal overflow-y-auto rounded-lg '
      closeIcon={null}
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[20px] font-bold leading-[24px]'>
            {editMode ? 'CHỈNH SỬA THƯƠNG HIỆU' : 'TẠO THƯƠNG HIỆU MỚI'}
          </h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>
            {editMode
              ? 'Vui lòng điền đầy đủ thông tin thương hiệu trước khi cập nhật'
              : 'Vui lòng điền đầy đủ thông tin của thương hiệu trước khi thêm'}
          </p>
        </div>
      }
      maskClosable={false}
    >
      <div className='flex-1 flex flex-col gap-1 p-4 max-h-[calc(100vh-180px)] overflow-y-auto bg-white rounded-lg '>
        <div className='grid grid-cols-[repeat(17,_minmax(0,_1fr))] gap-6 px-[24px] py-2 lg:min-h-[70vh] lg:max-h-[90vh] '>
          <BrandInfoSection
            form={form}
            onGenerate={onGenerate}
            isCopyMode={isCopyMode}
            codePrefix={codePrefix}
            setCodePrefix={setCodePrefix}
            isActive={isActive}
            setIsActive={setIsActive}
            onSubmit={handleSubmit}
            editMode={editMode}
          />
          <CategorySelectionSection
            checkedAvailableIds={checkedAvailableIds}
            setCheckedAvailableIds={setCheckedAvailableIds}
            form={form}
            categories={categories}
            availableCategories={availableCategories}
            setAvailableCategories={setAvailableCategories}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isOpen={isOpen}
            editMode={editMode || isCopyMode}
          />
        </div>
      </div>

      <div className='flex justify-center mt-4 gap-3'>
        <ButtonCommon color='none' size='md' radius='rounded' onClick={handleCancel}>
          Hủy
        </ButtonCommon>
        <ButtonCommon color='primary' size='md' radius='rounded' onClick={() => form.submit()} loading={isSubmitting}>
          {editMode ? 'Cập nhật thương hiệu' : 'Thêm thương hiệu'}
        </ButtonCommon>
      </div>
    </Modal>
  );
};

export default AddBrandModal;
